import { Group, Mesh, MeshBasicMaterial, MeshStandardMaterial, RingGeometry, Scene, Vector3 } from 'three';
import { pz } from 'Domain/pieces';
import { getTileGeometry, getTileMaterials, SIZES } from './assets';
import hexPrismGeometry, { ORIENTATION } from './geometry';
import addLights from './lighting';
import { allRenderedCells, cellToWorld, COLUMN_PITCH, directionToAngle, isPlayableCell, R, ROW_PITCH } from './layout';
import { hqPosition, lastScreenPosition, noteScreenPosition, setHand } from './flight';
import { AIM, BOARD, boardColors, HIGHLIGHT, HOVER, KEYLINE, previewStep } from './palette';
import getStage, { prefersReducedMotion } from './stage';
import createProjector, { boxStyle } from './view';
import createToken, { CARRY_LIFT } from './token';

// The board: a hexagonal tray with thirty-seven tiles inlaid in it, twenty-four ghost cells
// around the edge that exist only to be pointed at, and the pieces standing on top.
//
// The tray really is a hexagon — rows of 4,5,6,7,6,5,4 make one, with points east and west and
// flat edges north and south — which is a nicer thing to discover than to design.

const TILE_TOP = SIZES.tileHeight;

// Far enough from where it is going that it is worth letting it be drawn outside the board's own
// rectangle — a piece coming out of an HQ tray spends most of its journey over other things.
const TRAVELLING = 0.4;

// Legal cells stand up out of the tray like buttons. Y only, and small: it is the best tabletop
// reading of "you may go here", and being vertical it cannot move a single overlay box.
const TILE_RISE = R * 0.05;
const RISE_RATE = 18;

// How long a dead piece will wait to be let go before it goes anyway. Every path that arms a
// gesture bounds its own wait, so this is never reached in play — it exists so that a killer whose
// token disappears mid-gesture cannot leave a corpse animating for the rest of the game.
const UNCLAIMED = 1.6;

// Sized so the lip is about the same all the way round. The board's outline is already a
// flat-topped hexagon, so the tray is the same hexagon, larger. Its half-height is only 0.866 of
// its radius, hence solving for the taller of the two constraints rather than adding a margin to
// the wider one.
const PLINTH_RADIUS = (3 * ROW_PITCH + SIZES.tileRadius + R * 0.45) / Math.cos(Math.PI / 6);
const PLINTH_DEPTH = R * 0.75;

const shared = {};

function sharedAsset(key, build) {
	if (!shared[key]) {
		shared[key] = build();
	}

	return shared[key];
}

// A hexagonal annulus, pointy topped to sit square on a cell. RingGeometry lays out in XY from
// the +x axis, so it starts a quarter turn round and is then laid flat.
function hexRing(inner, outer) {
	const geometry = new RingGeometry(inner, outer, 6, 1, Math.PI / 2);
	geometry.rotateX(-Math.PI / 2);

	return geometry;
}

function flat(key, inner, outer, color, opacity) {
	return new Mesh(
		sharedAsset(`ring-${key}`, () => hexRing(inner, outer)),
		sharedAsset(
			`ringMaterial-${key}`,
			() => new MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false }),
		),
	);
}

// The geometry is shared by every skin — it is the same plinth — but the materials are cached per
// skin, because sharedAsset is a module-level cache and a key that ignored the skin would hand the
// second room the first room's colours.
function createPlinth(skin) {
	const colors = boardColors(skin);

	const plinth = new Mesh(
		sharedAsset('plinth', () =>
			hexPrismGeometry({
				radius: PLINTH_RADIUS,
				height: PLINTH_DEPTH,
				chamfer: R * 0.3,
				orientation: ORIENTATION.flat,
			}),
		),
		sharedAsset(`plinthMaterials-${skin}`, () => [
			new MeshStandardMaterial({ color: colors.plinth, roughness: 0.85, metalness: 0.1 }),
			new MeshStandardMaterial({ color: colors.plinthEdge, roughness: 0.4, metalness: 0.3 }),
			new MeshStandardMaterial({ color: colors.plinth, roughness: 0.85, metalness: 0.1 }),
		]),
	);

	plinth.position.y = -PLINTH_DEPTH;

	return plinth;
}

// Legal destinations are red on the flat board and stay red here. A rim and a wash rather than a
// solid fill: the tile underneath has to stay readable, because a legal cell is very often the
// cell a player is also trying to see a piece on. The dark shoulder outside the red is what makes
// one ring work on the palest tile and the darkest alike — red on #a1abb7 is about 1.5:1 on its
// own, which is not a signal.
//
// The wash is faint. Deploying a piece out of an HQ makes every empty cell legal, so it is the wash
// on thirty-seven tiles at once as often as it is the wash on two — and at anything heavier the
// board stops being a board and becomes a red rectangle.
//
// `signal` marks the two rings that carry the meaning, and therefore the two a spy's later steps
// recolour. The keyline is not one of them: it is the dark shoulder that makes any of these hues
// legible against both ends of the board, so it keeps its own colour whatever is inside it.
const HIGHLIGHT_RINGS = [
	{
		key: 'wash',
		inner: 0,
		outer: SIZES.tileRadius * 0.84,
		color: HIGHLIGHT,
		signal: true,
		opacity: 0.09,
		lift: 0.006,
		order: 1,
	},
	{
		key: 'rim',
		inner: SIZES.tileRadius * 0.855,
		outer: SIZES.tileRadius * 0.9,
		color: HIGHLIGHT,
		signal: true,
		opacity: 0.8,
		lift: 0.008,
		order: 2,
	},
	{
		key: 'keyline',
		inner: SIZES.tileRadius * 0.9,
		outer: SIZES.tileRadius * 0.912,
		color: KEYLINE,
		opacity: 0.85,
		lift: 0.008,
		order: 3,
	},
];

// A cell a spy could only reach on a later move is the same three rings in that step's own colour.
// Materials rather than a second set of meshes: they are shared and cached per level, so a preview
// costs one assignment per ring and the board still holds thirty-seven highlight groups in all.
function ringMaterial(ring, level) {
	return sharedAsset(`ringMaterial-${ring.key}-${level}`, () => {
		const step = level && previewStep(level);

		return new MeshBasicMaterial({
			color: step && ring.signal ? step.color : ring.color,
			transparent: true,
			opacity: step ? ring.opacity * step.fade : ring.opacity,
			depthWrite: false,
		});
	});
}

function createHighlight() {
	const group = new Group();

	const rings = HIGHLIGHT_RINGS.map(ring => {
		const mesh = new Mesh(
			sharedAsset(`ring-${ring.key}`, () => hexRing(ring.inner, ring.outer)),
			ringMaterial(ring, 0),
		);

		mesh.position.y = TILE_TOP + ring.lift;
		mesh.renderOrder = ring.order;

		return mesh;
	});

	group.add(...rings);
	group.visible = false;

	return Object.assign(group, {
		setLevel(level) {
			rings.forEach((mesh, at) => {
				mesh.material = ringMaterial(HIGHLIGHT_RINGS[at], level);
			});
		},
	});
}

// Where a piece may be POINTED, which is never the same colour as where it may go. Six of them,
// reused: a piece only ever aims from one cell at a time.
const AIM_MARKERS = 6;

function createAimMarker() {
	const marker = flat('aim', SIZES.tileRadius * 0.66, SIZES.tileRadius * 0.86, AIM, 0.62);

	marker.renderOrder = 2;
	marker.visible = false;

	return marker;
}

export default function createBoardScene(element, skin) {
	const scene = new Scene();
	const { well: color, wellAlpha: alpha } = boardColors(skin);
	const well = Object.freeze({ color, alpha });

	addLights(scene);
	scene.add(createPlinth(skin));

	const cells = allRenderedCells();
	const tiles = new Map();
	const highlights = new Map();

	for (const cell of cells) {
		if (!cell.playable) {
			// The ring past the edge is not a place, it is somewhere to point at, and it hangs off
			// the side of the tray with nothing underneath it. Drawing anything there at rest reads
			// as a mistake rather than as a hint — the flat board draws nothing either. It gets its
			// moment when a piece is actually being aimed, as one of the aim markers below.
			continue;
		}

		const tile = new Mesh(getTileGeometry(), getTileMaterials(cell.row, cell.cell));
		tile.position.set(cell.x, 0, cell.z);

		// Parented to the tile, so a legal cell's ring rises with the tile it belongs to instead
		// of being left behind on the tray.
		const highlight = createHighlight();
		tile.add(highlight);

		scene.add(tile);
		tiles.set(`${cell.row}-${cell.cell}`, { tile, rise: 0, wanted: 0 });
		highlights.set(`${cell.row}-${cell.cell}`, highlight);
	}

	// Row 3 cell 3 is the only cell the flat board paints with a radial gradient where every other
	// cell is linear. It keeps a mark of its own: a brass boss set into the tile — an inlay, not a
	// light, so a legal-move ring on that cell still wins the eye.
	const centreMark = new Mesh(
		sharedAsset('centreGeometry', () => hexRing(SIZES.tileRadius * 0.14, SIZES.tileRadius * 0.3)),
		sharedAsset(
			'centreMaterial',
			() => new MeshStandardMaterial({ color: BOARD.centre, roughness: 0.35, metalness: 0.45 }),
		),
	);
	// Parented to its tile rather than placed in world space, so it rides up with the tile when
	// row 3 cell 3 is a legal destination instead of being left behind inside it.
	centreMark.position.set(0, TILE_TOP + 0.004, 0);
	tiles.get('3-3').tile.add(centreMark);

	// The flat board lightened the cell under the pointer by five percent. Cheap, and the only
	// thing that ever said which of thirty-seven near-identical hexagons the pointer owned — so it
	// comes back here as a thin cool rim, kept clear of red (may go) and blue (may point).
	const hoverRing = flat('hover', SIZES.tileRadius * 0.9, SIZES.tileRadius * 0.99, HOVER, 0.5);
	hoverRing.position.y = TILE_TOP + 0.01;
	hoverRing.renderOrder = 1;
	hoverRing.visible = false;

	const aimMarkers = [];
	for (let marker = 0; marker < AIM_MARKERS; marker++) {
		const mesh = createAimMarker();

		aimMarkers.push(mesh);
		scene.add(mesh);
	}

	// Bounds deliberately include the ring past the edge. Those cells are clickable, and a
	// clickable box that escaped its container would sit invisibly over an HQ button.
	// Both corners at BOTH heights. A point higher up is a point nearer a camera looking down at
	// 52 degrees, so it projects further out — bounding the box at the tray and the tile top
	// separately would let an overlay box escape the container the fit was supposed to keep it in.
	const bounds = [];
	for (const cell of cells) {
		for (const y of [0, TILE_TOP]) {
			bounds.push(
				new Vector3(cell.x - COLUMN_PITCH / 2, y, cell.z - ROW_PITCH / 2),
				new Vector3(cell.x + COLUMN_PITCH / 2, y, cell.z + ROW_PITCH / 2),
			);
		}
	}

	const projector = createProjector({ bounds, padding: 0.02 });
	const tokens = new Map();
	// Which cell each token is standing on, so it can ride up with a tile that rises under it.
	const standingOn = new Map();
	// Who is already dead, so that a kill is a TRANSITION and not a state. Without it every state
	// change would re-announce every kill of the game so far.
	const dead = new Set();
	// Pieces that have been killed and are still on screen: held where they fell until the blow
	// that killed them lands, then carried off to the killer's HQ. Kept apart from `tokens`
	// because everything there is a piece the game still has on the board.
	const leaving = new Map();
	let opened = false;
	let signature = null;
	let carrying = null;
	let travelling = false;

	/**
	 * What has just been killed and by whom, from the only record of it the game keeps: the
	 * `killedById` a corpse carries. Nothing dispatches a kill and nothing needs to — the state
	 * says who did it.
	 *
	 * Three things it has to get right, each of which is a real path through the rules:
	 *
	 * - **One shot, one move.** A dead CEO takes its team's whole undeployed half with it, and
	 *   every one of those corpses names the same killer. That is one gesture to watch and six
	 *   pieces to carry off, not six gestures — hence victims grouped under the killer.
	 * - **A piece can come back to life.** A snipe rolls the board back to before the move it
	 *   answered, so whatever that move killed is alive again — and can be killed again later.
	 *   Hence a set rebuilt from the state each time rather than one that only ever grows.
	 * - **A scene opens on a game already in progress**, after a rejoin, a lost WebGL context or
	 *   `?test=`. Every kill of that game is new to this Set and none of them is news, so the
	 *   first pass seeds and announces nothing.
	 */
	function killsAmong(pieces) {
		const kills = [];

		for (const piece of pieces) {
			if (opened && piece.killed && piece.killedById && !dead.has(piece.id)) {
				const already = kills.find(kill => kill.killerId === piece.killedById);

				if (already) {
					already.victimIds.push(piece.id);
				} else {
					kills.push({ killerId: piece.killedById, victimIds: [piece.id] });
				}
			}
		}

		dead.clear();

		for (const piece of pieces) {
			if (piece.killed) {
				dead.add(piece.id);
			}
		}

		opened = true;

		return kills;
	}

	/**
	 * Send a corpse home to the HQ of whoever killed it.
	 *
	 * The journey is the deploy in reverse and uses the same machinery: the tray publishes where
	 * it is on the page, this turns that back into a point on the board's own plane, and the piece
	 * is simply told to go there — so it lofts across the table on the arc every travelling piece
	 * uses. `overlay()` is what lets it be drawn over the gap and over the tray it is heading for.
	 *
	 * A team whose tray is not on screen has no published position. The corpse then goes where it
	 * stands rather than nowhere, which is the same thing the board did before any of this existed.
	 */
	function releaseCorpse(pieceId, team) {
		const entry = leaving.get(pieceId);

		if (!entry || entry.released) {
			return;
		}

		entry.released = true;

		const home = hqPosition(team);
		const at = home && toBoard(home.x, home.y, TILE_TOP);

		if (at) {
			entry.token.set({ ...entry.token.state(), x: at.x, z: at.z, selected: false, snipe: false, buffed: false });
		}

		entry.token.leave();
	}

	// A point in the page, on the plane a token's base sits on. Everything the hand does goes
	// through this: the pointer is in the document and the piece is in the scene, and the camera is
	// the only thing that relates them.
	function toBoard(clientX, clientY, planeY) {
		const rect = element.getBoundingClientRect();

		if (rect.width < 1 || rect.height < 1) {
			return null;
		}

		return projector.unproject(clientX - rect.left, clientY - rect.top, planeY);
	}

	// The frame loop settles when nothing is animating, so anything that changes the scene from
	// outside it — a piece being picked up or put down — has to say so.
	function nudge() {
		const stage = getStage();

		if (stage) {
			stage.invalidateAll();
		}
	}

	function screenOf(token) {
		const rect = element.getBoundingClientRect();
		const at = projector.project(token.object.position.x, TILE_TOP, token.object.position.z);

		return { x: rect.left + at.x, y: rect.top + at.y };
	}

	// Pieces stand on the tiles, not in them. One group carries the lot up to the tile surface so
	// a token never has to know how thick the board it is standing on happens to be.
	const standing = new Group();
	standing.position.y = TILE_TOP;
	scene.add(standing, hoverRing);

	function tokenFor(pieceId) {
		if (!tokens.has(pieceId)) {
			const token = createToken(pieceId);

			// A piece arriving on the board has usually been somewhere visible a moment ago — in its
			// HQ socket, or under the pointer at the end of a drag. Starting it there and letting it
			// fly to its cell is what turns "it is now over here" into a move you can follow.
			const from = lastScreenPosition(pieceId);
			const at = from && toBoard(from.x, from.y, TILE_TOP);

			tokens.set(pieceId, token);
			standing.add(token.object);

			if (at) {
				token.placeAt(at.x, at.z, CARRY_LIFT * 0.5);
			}
		}

		return tokens.get(pieceId);
	}

	const board = {
		scene,
		camera: projector.camera,

		// Drawn after the trays, so a piece being carried passes over them rather than under.
		order: 1,

		resize(width, height) {
			projector.resize(width, height);
		},

		extent: projector.extent,

		// The recess the whole board section is sunk into, painted by clearing the view's own
		// rectangle before anything is drawn in it. It has to come from here rather than from a CSS
		// background on the board element for the reason the note in theme/tokens.js gives: the canvas
		// is under `.game`, so a background there would be a filter over every tile rather than a
		// surface beneath them. Frozen and returned as-is, because the stage reads it every frame.
		well: () => well,

		// While a piece is in the air it has to be drawn outside the board's own rectangle — over
		// the HQ tray it came out of, over the gap between them. Saying so here is what opens the
		// scissor and the viewport both; the rest of the time the board is scissored down to what it
		// actually paints.
		overlay: () => !!carrying || travelling,

		// And this is what keeps the board itself still when that happens — the projection is
		// re-framed into the wider viewport rather than stretched into it.
		widen: projector.widen,

		/**
		 * THE HAND
		 *
		 * Picking a piece up, carrying it, and letting it go. The DOM still decides everything —
		 * which piece was pressed, which cell it was released over, whether that is a legal move —
		 * and this only draws the answer. Nothing here dispatches anything.
		 */

		grab(pieceId) {
			const onBoard = tokens.has(pieceId);
			const token = tokenFor(pieceId);

			carrying = { pieceId, onBoard };
			token.set({ ...token.state(), carried: true });
			nudge();

			return true;
		},

		carryTo(clientX, clientY) {
			const token = carrying && tokens.get(carrying.pieceId);
			const at = token && toBoard(clientX, clientY, TILE_TOP + CARRY_LIFT);

			if (!at) {
				return;
			}

			// Straight to the pointer with no easing at all. A carried piece that lags behind the
			// cursor feels like a webpage; one that is exactly under it feels like a game.
			token.placeAt(at.x, at.z, CARRY_LIFT);
			noteScreenPosition(carrying.pieceId, clientX, clientY);
		},

		drop() {
			if (!carrying) {
				return;
			}

			const { pieceId, onBoard } = carrying;
			const token = tokens.get(pieceId);

			carrying = null;
			nudge();

			if (!token) {
				return;
			}

			if (onBoard) {
				// It was already a piece on the board, so it stays one: it settles back down where
				// it was until the next state says otherwise.
				token.set({ ...token.state(), carried: false });

				return;
			}

			// It came out of an HQ and the game has not accepted it yet. Put it away — the tray will
			// show it again — and if the drop turns out to have been legal, the state that follows
			// builds it afresh from the point it was let go of, so the flight carries on unbroken.
			standing.remove(token.object);
			token.dispose();
			tokens.delete(pieceId);
		},

		/**
		 * Where each cell's invisible DOM twin has to go, ready to hand to an element's `style`.
		 *
		 * Boxes are a column pitch wide and a row pitch tall, which tiles the plane exactly: a
		 * cell's right edge is its neighbour's left edge to the pixel, and its bottom edge is the
		 * next row's top edge. Using the hexagons' own bounding boxes instead would overlap
		 * adjacent rows by a quarter of their height, and which of two invisible boxes a click
		 * landed on would come down to DOM order.
		 */
		layout() {
			const boxes = {};

			for (const cell of cells) {
				boxes[`${cell.row}-${cell.cell}`] = boxStyle(
					projector,
					cell.x,
					cell.z,
					TILE_TOP,
					COLUMN_PITCH / 2,
					ROW_PITCH / 2,
				);
			}

			return boxes;
		},

		/** @returns whether anything visible changed, which decides whether the board repaints. */
		setState({ pieces, highlightedPositions, previewPositions = [], snipe, aim, hovered }) {
			const next = [
				pieces
					.filter(piece => piece.position && !piece.killed)
					.map(
						piece =>
							`${piece.id}@${piece.position}/${piece.selectedDirection}${piece.selected ? 's' : ''}${
								piece.highlight ? 'h' : ''
							}${piece.buffed ? 'b' : ''}`,
					)
					.join(),
				highlightedPositions.join(';'),
				previewPositions.map(positions => positions.join(';')).join('+'),
				snipe ? 'snipe' : '',
				aim ? `${aim.from}>${aim.directions.join(';')}` : '',
			].join('|');

			// Read before the early return, and it is what defeats it: a kill is not in the
			// signature — the signature is what is on the board, and a kill is something that has
			// just left it. Reading it here also makes it idempotent, so a state change that
			// happens to repeat cannot replay a gesture.
			const kills = killsAmong(pieces);
			const fresh = new Set(kills.flatMap(kill => kill.victimIds));

			if (next === signature && !kills.length) {
				return false;
			}

			signature = next;

			for (const highlight of highlights.values()) {
				highlight.visible = false;
			}
			for (const entry of tiles.values()) {
				entry.wanted = 0;
			}

			// Where a spy's later steps could land, a colour per move away. Drawn before the legal
			// cells and without the rise: the tile standing up out of the tray is what "you may go
			// here" means, and a cell reachable both now and later has to end up saying the nearer
			// of the two. The domain hands these back disjoint, so the order is a belt as well.
			previewPositions.forEach((positions, level) => {
				for (const [row, cell] of positions) {
					const highlight = highlights.get(`${row}-${cell}`);

					if (highlight) {
						highlight.setLevel(level + 1);
						highlight.visible = true;
					}
				}
			});

			for (const [row, cell] of highlightedPositions) {
				const key = `${row}-${cell}`;
				const highlight = highlights.get(key);

				if (highlight) {
					highlight.setLevel(0);
					highlight.visible = true;
					tiles.get(key).wanted = TILE_RISE;
				}
			}

			// Aim markers are placed by world offset rather than by cell, because half of them
			// land on the ring past the edge — which has no coordinates the domain will hand back.
			for (const marker of aimMarkers) {
				marker.visible = false;
			}

			if (aim) {
				const from = cellToWorld(aim.from[0], aim.from[1]);

				aim.directions.slice(0, AIM_MARKERS).forEach((direction, at) => {
					const bearing = (directionToAngle(direction) * Math.PI) / 180;
					const marker = aimMarkers[at];

					marker.position.set(
						from.x + COLUMN_PITCH * Math.sin(bearing),
						TILE_TOP + 0.01,
						from.z - COLUMN_PITCH * Math.cos(bearing),
					);
					marker.visible = true;
				});
			}

			const under = hovered && tiles.get(`${hovered[0]}-${hovered[1]}`);

			hoverRing.visible = !!under;
			if (under) {
				const at = cellToWorld(hovered[0], hovered[1]);

				hoverRing.position.set(at.x, TILE_TOP + 0.01, at.z);
			}

			const onBoard = new Set();

			for (const piece of pieces) {
				if (piece.killed || !piece.position || !isPlayableCell(piece.position[0], piece.position[1])) {
					continue;
				}

				// It was dead a moment ago and it is not any more — a snipe rolled the board back
				// past the move that killed it. Take its token back off the journey it was on
				// rather than building a second one for the same piece, and it flies home from
				// wherever the corpse had got to.
				//
				// This is a net rather than a path anybody walks: a corpse is off the board in
				// just over half a second and a table takes rather longer than that to reach for
				// `SNIPE`, so in practice the corpse is long gone and `tokenFor` below builds a
				// fresh token at the cell. Both endings leave exactly one token for one piece,
				// which is the only thing that actually matters here — traced both ways in a
				// browser rather than assumed.
				const returning = leaving.get(piece.id);

				if (returning) {
					returning.token.revive();
					tokens.set(piece.id, returning.token);
					leaving.delete(piece.id);
				}

				const { x, z } = cellToWorld(piece.position[0], piece.position[1]);
				const token = tokenFor(piece.id);

				onBoard.add(piece.id);
				standingOn.set(piece.id, `${piece.position[0]}-${piece.position[1]}`);

				// A carried piece is where the pointer is, not where the game thinks it is. It gets
				// its cell back the moment it is let go of.
				if (carrying && carrying.pieceId === piece.id) {
					continue;
				}
				token.set({
					x,
					z,
					direction: piece.selectedDirection,
					selected: piece.selected,
					// A lit sniper is the other half of a snipe, and the only piece the flat board
					// brightens for a reason that is not selection.
					snipe: !!snipe && piece.highlight && pz.isSniper(piece.id),
					buffed: piece.buffed,
				});
			}

			// A piece that left the board was killed, or was rolled back by a sniper. A piece that
			// was just KILLED is held where it fell instead, until the blow that killed it lands
			// and it can be carried off; everything else stops existing here rather than sliding
			// off. The exception is the piece in the player's hand: the game does not think it is
			// on the board — it is not, yet — but it is the one thing on screen the player is
			// actually holding.
			for (const [pieceId, token] of tokens) {
				if (carrying && carrying.pieceId === pieceId) {
					continue;
				}

				if (!onBoard.has(pieceId)) {
					tokens.delete(pieceId);
					standingOn.delete(pieceId);

					if (fresh.has(pieceId)) {
						leaving.set(pieceId, { token, released: false, waited: 0 });
					} else {
						standing.remove(token.object);
						token.dispose();
					}
				}
			}

			// After the cleanup, because a killer is not always a piece that was already standing
			// here: a sniper killed by the very move it saw is brought back to life by its own
			// shot, and its token is built by the loop above in the same pass that arms it.
			//
			// The corpses go when the gesture says so, not now. An agent that has a cell to cross
			// before it arrives would otherwise be running its point through an empty tile, which
			// is the whole of what makes a charge read as a charge. If there is no gesture to wait
			// for — no token, an unknown type, or a player who has asked for less movement — they
			// go immediately, which is what the board did before any of this existed.
			for (const { killerId, victimIds } of kills) {
				const token = tokens.get(killerId);
				const team = pz.getTeam(killerId);
				const release = () => victimIds.forEach(victimId => releaseCorpse(victimId, team));

				if (!token || !token.kill(pz.getType(killerId), release)) {
					release();
				}
			}

			return true;
		},

		update(delta) {
			// A piece in hand is moving by definition, whatever the tweens think: the pointer is
			// what moves it, and the pointer does not go through this. Without saying so the loop
			// settles and the carried piece freezes wherever it was last drawn.
			let animating = !!carrying;
			let inFlight = !!carrying;

			const snap = prefersReducedMotion();

			for (const entry of tiles.values()) {
				if (!snap && Math.abs(entry.rise - entry.wanted) > TILE_RISE * 0.02) {
					entry.rise += (entry.wanted - entry.rise) * (1 - Math.exp(-RISE_RATE * delta));
					animating = true;
				} else {
					entry.rise = entry.wanted;
				}

				entry.tile.position.y = entry.rise;
			}

			for (const [pieceId, token] of tokens) {
				if (token.update(delta)) {
					animating = true;
				}

				// After update, which puts the token back on the tray's surface. A legal cell is
				// often an occupied one — that is how an agent kills — so the piece standing there
				// has to come up with the tile rather than be left half sunk in it.
				const tile = tiles.get(standingOn.get(pieceId));
				token.object.position.y = tile ? tile.rise : 0;

				if (token.distanceToGo() > TRAVELLING) {
					inFlight = true;
				}

				// So that a piece picked up off the board starts its flight from where it is drawn,
				// not from where it was last laid out.
				const at = screenOf(token);
				noteScreenPosition(pieceId, at.x, at.y);
			}

			// The dead, on their way to somebody's HQ. Always animating while any of them exists:
			// one is either waiting for the blow that killed it to land or crossing the table, and
			// neither is something the tweens on the token can be asked about.
			for (const [pieceId, entry] of leaving) {
				entry.token.update(delta);
				animating = true;
				// It has to be drawn outside the board's own rectangle for most of the journey, so
				// this is what opens the scissor and the viewport for it — the same widening a
				// piece coming the other way out of a tray already needs.
				inFlight = true;

				// A corpse cannot be left holding on for a strike that is never coming. The wait is
				// normally a third of a second, and every path that arms a gesture bounds itself —
				// but a killer whose token is disposed mid-gesture would take its victim's frame
				// loop down with it, so the corpse gives up on its own and goes.
				if (!entry.released) {
					entry.waited += delta;

					if (entry.waited >= UNCLAIMED) {
						releaseCorpse(pieceId, undefined);
					}
				}

				if (entry.token.gone()) {
					standing.remove(entry.token.object);
					entry.token.dispose();
					leaving.delete(pieceId);
				}
			}

			travelling = inFlight;

			return animating;
		},

		dispose() {
			setHand(null);

			for (const token of tokens.values()) {
				token.dispose();
			}
			tokens.clear();

			for (const entry of leaving.values()) {
				entry.token.dispose();
			}
			leaving.clear();
		},
	};

	// The drag controller sits above the whole app; the board is the only thing that can draw a
	// piece anywhere on screen, so it is the one that offers to.
	setHand(board);

	return board;
}
